import { useCallback, useRef } from 'react'
import { useInventoryActions as useInventoryStoreActions } from '../store'

export const useInventoryItemActions = (setBetAmount?: (betAmount: string) => void) => {
  const { activateItem, deactivateItem, closeInventoryPanel } = useInventoryStoreActions()
  const recentlyDeactivatedRef = useRef<Set<string>>(new Set())

  const handleActivateItem = useCallback((
    e: React.MouseEvent | React.PointerEvent, 
    itemId: string, 
    isApplicableToGame: boolean
  ) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!isApplicableToGame) return
    
    try {
      activateItem(itemId)
      closeInventoryPanel()
    } catch (error) {
      console.error('Error using booster:', error)
    }
  }, [activateItem, closeInventoryPanel])

  const handleActivateBonus = useCallback((
    e: React.MouseEvent | React.PointerEvent, 
    itemId: string, 
    amount: number, 
    isButtonDisabled: boolean
  ) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (isButtonDisabled) return
    
    // Prevent immediate re-activation after deactivation
    if (recentlyDeactivatedRef.current.has(itemId)) {
      return
    }
    
    try {
      activateItem(itemId)
      if (setBetAmount) {
        setBetAmount(amount.toString())
      }
      closeInventoryPanel()
    } catch (error) {
      console.error('Error using bonus:', error)
    }
  }, [activateItem, setBetAmount, closeInventoryPanel])

  const handleDeactivateItem = useCallback((
    e: React.MouseEvent | React.PointerEvent, 
    itemId: string
  ) => {
    e.preventDefault()
    e.stopPropagation()
    
    try {
      deactivateItem(itemId)
      
      // Mark as recently deactivated to prevent immediate re-activation
      recentlyDeactivatedRef.current.add(itemId)
      
      // Clear the protection after a short delay
      setTimeout(() => {
        recentlyDeactivatedRef.current.delete(itemId)
      }, 500) // 500ms protection window
    } catch (error) {
      console.error('Error unclaiming bonus:', error)
    }
  }, [deactivateItem])

  return {
    handleActivateItem,
    handleActivateBonus,
    handleDeactivateItem
  }
}